/**
 * Blast radius of the asset a case is about.
 *
 * NOT AN ATTACK FLOW. The layout is radial and every connector is a plain line:
 * no arrowheads, no left-to-right ordering, nothing that could be read as a
 * sequence of events. This answers "what does this asset touch", which has no
 * chronology — conflating it with a timeline of the escalation would invent a
 * narrative the data does not contain.
 *
 * A PREVIEW, NOT A SECOND VISUALIZER. It shows the root and what it reaches at
 * a glance, then hands off. The full canvas is one click away and already does
 * exploration properly; a second explorable graph here would be a second
 * renderer to keep in step with it.
 */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CaseReach, ResolvedOrigin } from '../../services/case-blast-radius.service';
import { NoOriginReason } from '../../utils/case-origin';
import { NODE_KIND_COLORS, NODE_KIND_LABELS, PivotNodeKind } from '../../../inventory/pivot/models/pivot.models';
import { PivotIntent, intentsFor } from '../../../inventory/pivot/models/pivot-intents';

/** SVG user units across the canvas. The <svg> is pinned to exactly this many
 *  CSS pixels so one user unit is always one pixel: at `width: 100%` the
 *  viewBox scaled with the container and the connectors drifted away from the
 *  DOM nodes they were drawn to. */
const CANVAS_WIDTH = 260;

/** Vertical room the node boxes need beyond the span between their centres. */
const NODE_ALLOWANCE = 84;

/**
 * An offset from the canvas centre, as CSS.
 *
 * The sign goes into the operator: `calc(50% + -33px)` leans on the browser
 * accepting a signed operand after an operator, which is not something to
 * depend on for whether a node lands on its connector.
 */
/**
 * The intent that opens one relation group.
 *
 * Synthesised rather than looked up: the named presets in PIVOT_INTENTS answer
 * questions an analyst asks in words, while a mark on this map already IS the
 * group — clicking "14 Endpoints" can only mean "show me those endpoints".
 */
function groupIntent(kind: PivotNodeKind): PivotIntent {
  return {
    key: `group-${kind}`,
    label: NODE_KIND_LABELS[kind],
    description: `Open the Visualizer focused on this entity's ${NODE_KIND_LABELS[kind].toLowerCase()} relationships`,
    group: kind,
  };
}

function fromCentre(offset: number): string {
  const n = Math.abs(offset).toFixed(1);
  return offset < 0 ? `calc(50% - ${n}px)` : `calc(50% + ${n}px)`;
}

/** A relation kind the root touches, placed on the circle. */
interface RelationMark {
  kind: PivotNodeKind;
  label: string;
  count: number;
  color: string;
  x: number;
  y: number;
  left: string;
  top: string;
  intent: PivotIntent;
}

interface PlacedNode {
  resolved: ResolvedOrigin;
  /** SVG-space offset from the root, used to draw the connector. */
  x: number;
  y: number;
  /** The same point as CSS, measured from the canvas centre rather than its
   *  top-left corner — the DOM nodes and the SVG do not share an origin. */
  left: string;
  top: string;
  color: string;
  kindLabel: string;
  intents: PivotIntent[];
  isRoot: boolean;
}

@Component({
  selector: 'app-case-blast-radius',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './case-blast-radius.component.html',
  styleUrl: './case-blast-radius.component.scss',
})
export class CaseBlastRadiusComponent {
  public resolved = input.required<ResolvedOrigin[]>();
  public loading = input<boolean>(false);
  /** What the root actually touches. Null until loaded, or if it never resolved. */
  public reach = input<CaseReach | null>(null);
  /** Why there is nothing to show, when there is nothing to show. */
  public noOriginReason = input<NoOriginReason>('no-asset-named');

  public explore = output<{ resolved: ResolvedOrigin; intent: PivotIntent }>();

  public readonly kindLabels = NODE_KIND_LABELS;

  public readonly root = computed<ResolvedOrigin | null>(() => this.resolved()[0] ?? null);

  /**
   * The other assets the case names.
   *
   * Listed BESIDE the map, never drawn on it. They are entities the same ticket
   * mentions, which is not the same claim as an edge in the graph — the two may
   * have no relationship at all. Drawing a connector to them said they did.
   */
  public readonly alsoNamed = computed<ResolvedOrigin[]>(() => this.resolved().slice(1));

  public readonly hasAnything = computed<boolean>(() => this.resolved().length > 0);

  /**
   * Positions, on a circle around the root.
   *
   * Angles are distributed evenly and start at the top. There is no meaning in
   * WHICH position a satellite takes — only that it is beside the root rather
   * than after it, which is the distinction between a blast radius and a flow.
   */
  /**
   * Canvas geometry.
   *
   * Marks are laid out around the root, then the whole group is shifted so that
   * what is DRAWN sits in the middle of the box. Centring the root instead left
   * the unused half of the circle as dead space. The canvas is then sized to
   * the group's vertical extent rather than to a fixed height.
   *
   * Elliptical, not circular: a mark is far wider than it is tall, so a circle
   * that clears its neighbours horizontally still stacks them vertically.
   */
  private readonly marksCount = computed<number>(() => this.reach()?.relations.length ?? 0);
  private readonly radiusX = computed<number>(() => (this.marksCount() <= 2 ? 100 : 124));
  private readonly radiusY = computed<number>(() => (this.marksCount() <= 2 ? 68 : 82));

  private readonly rawOffsets = computed<{ x: number; y: number }[]>(() => {
    const n = this.marksCount();
    const rx = this.radiusX();
    const ry = this.radiusY();
    return Array.from({ length: n }, (_, i) => {
      // Starts at the top and goes round. WHICH slot a kind takes carries no
      // meaning — only that it is beside the root rather than after it, which
      // is the difference between a blast radius and a flow.
      const angle = n === 1 ? -Math.PI / 2 : (i / n) * Math.PI * 2 - Math.PI / 2;
      return { x: Math.cos(angle) * rx, y: Math.sin(angle) * ry };
    });
  });

  /** How far to shift everything so the drawn group is vertically centred. */
  private readonly groupShiftY = computed<number>(() => {
    const ys = [0, ...this.rawOffsets().map((o) => o.y)];
    return -(Math.min(...ys) + Math.max(...ys)) / 2;
  });

  public readonly canvasHeight = computed<number>(() => {
    const ys = [0, ...this.rawOffsets().map((o) => o.y)];
    return Math.max(140, Math.max(...ys) - Math.min(...ys) + NODE_ALLOWANCE);
  });

  public readonly viewBox = computed<string>(
    () => `${-CANVAS_WIDTH / 2} ${-this.canvasHeight() / 2} ${CANVAS_WIDTH} ${this.canvasHeight()}`
  );

  /**
   * The relation kinds the root touches, as placed marks.
   *
   * These are real edges out of the graph, so they are the only things on this
   * canvas a connector is drawn to.
   */
  public readonly marks = computed<RelationMark[]>(() => {
    const relations = this.reach()?.relations ?? [];
    const shift = this.groupShiftY();
    return relations.map((r, i) => {
      const { x } = this.rawOffsets()[i];
      const y = this.rawOffsets()[i].y + shift;
      return {
        kind: r.kind,
        label: NODE_KIND_LABELS[r.kind],
        count: r.total,
        color: NODE_KIND_COLORS[r.kind],
        x,
        y,
        left: fromCentre(x),
        top: fromCentre(y),
        intent: groupIntent(r.kind),
      };
    });
  });

  public readonly rootNode = computed<PlacedNode | null>(() => {
    const r = this.root();
    if (!r) return null;
    return {
      resolved: r,
      x: 0,
      y: this.groupShiftY(),
      left: '50%',
      top: fromCentre(this.groupShiftY()),
      color: NODE_KIND_COLORS[r.origin.kind as PivotNodeKind],
      kindLabel: NODE_KIND_LABELS[r.origin.kind as PivotNodeKind],
      intents: intentsFor(r.origin.kind as PivotNodeKind),
      isRoot: true,
    };
  });

  /**
   * The headline number: how far the root reaches.
   *
   * Null while the degree call is in flight or when the root did not resolve —
   * rendered as nothing rather than as zero, because "we do not know yet" and
   * "reaches nothing" are opposite claims about an asset.
   */
  public readonly rootDegree = computed<number | null>(() => this.reach()?.total ?? null);

  public displayName(r: ResolvedOrigin): string {
    return r.resolvedName ?? r.origin.name;
  }

  public isResolved(r: ResolvedOrigin): boolean {
    return r.entityId !== null;
  }

  public onExplore(resolved: ResolvedOrigin, intent: PivotIntent): void {
    if (!this.isResolved(resolved)) return;
    this.explore.emit({ resolved, intent });
  }

  /** Opening a relation mark focuses the Visualizer on that group. */
  /** Opening a co-named asset re-roots the map on it, in the Visualizer. */
  public onExploreOther(other: ResolvedOrigin): void {
    if (!this.isResolved(other)) return;
    const intents = intentsFor(other.origin.kind as PivotNodeKind);
    if (!intents.length) return;
    this.explore.emit({ resolved: other, intent: intents[0] });
  }

  public colorFor(o: ResolvedOrigin): string {
    return NODE_KIND_COLORS[o.origin.kind as PivotNodeKind];
  }

  /** True while the reach lookup is still in flight, so an empty circle is not
   *  mistaken for "reaches nothing". */
  public reachLoading = input<boolean>(false);

  public onExploreMark(mark: RelationMark): void {
    const r = this.root();
    if (!r || !this.isResolved(r)) return;
    this.explore.emit({ resolved: r, intent: mark.intent });
  }

  /** Empty-state copy, chosen by WHY there is no root. */
  public readonly emptyTitle = computed(() =>
    this.noOriginReason() === 'ip-only' ? 'No tracked asset on this case' : 'No asset identified on this case'
  );

  public readonly emptyBody = computed(() =>
    this.noOriginReason() === 'ip-only'
      ? 'This alert is about network addresses rather than an asset in your inventory, so there is no blast radius to draw from it.'
      : 'This case does not name an endpoint, account or application that could be matched to your inventory.'
  );
}
